import React from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import { Link } from "react-router-dom";
import { Mail, Search, CheckCircle, XCircle } from "lucide-react";
import { fetchCustomersName } from "../actions/customers";
import { fetchEmailTemplates, updateEmailTemplate } from "../actions/emailTemplates";
import LoadingProgress from "../components/LoadingProgress";
import "../styles/EmailTemplatesView.css";

class EmailTemplatesView extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      searchTerm: "",
    };

    this.handleActiveToggle = this.handleActiveToggle.bind(this);
  }

  handleActiveToggle(template, e) {
    e.preventDefault();
    e.stopPropagation();
    const { updateEmailTemplate } = this.props;
    const newActiveValue = !template.is_active;
    updateEmailTemplate(template._id, { is_active: newActiveValue });
  }

  componentDidMount() {
    this.props.fetchCustomersName();
    this.props.fetchEmailTemplates();
  }

  componentDidUpdate(prevProps) {
    const { emailTemplatesNeedRefresh, customersNeedRefresh } = this.props;

    if (emailTemplatesNeedRefresh && !prevProps.emailTemplatesNeedRefresh) {
      this.props.fetchEmailTemplates();
    }

    if (customersNeedRefresh && !prevProps.customersNeedRefresh) {
      this.props.fetchCustomersName();
    }
  }

  handleSearchChange = (e) => {
    this.setState({ searchTerm: e.target.value });
  };

  getFilteredCustomers() {
    const { customers, emailTemplates } = this.props;
    const { searchTerm } = this.state;

    if (!customers || !emailTemplates) {
      return [];
    }

    // Filter only GENERAL and license type customers
    let filtered = customers.filter((customer) => customer.Type === "GENERAL" || customer.Type === "LICENCIA_BASICA" || customer.Type === "LICENCIA_PRO");

    // Create a map of customer_id to template for quick lookup
    const templateMap = new Map();
    emailTemplates.forEach((template) => {
      templateMap.set(template.customer_id, template);
    });

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (customer) =>
          customer.Fullname && customer.Fullname.toLowerCase().includes(search),
      );
    }

    // Map to include template status
    const mappedCustomers = filtered.map((customer) => ({
      ...customer,
      hasTemplate: templateMap.has(customer._id),
      template: templateMap.get(customer._id),
    }));

    // Sort: customers with templates first
    return mappedCustomers.sort((a, b) => {
      if (a.hasTemplate === b.hasTemplate) return 0;
      return a.hasTemplate ? -1 : 1;
    });
  }

  render() {
    const { loading, error, role } = this.props;

    // Only admins can access this view
    if (role !== "ADMIN") {
      return (
        <div className="email-templates-view">
          <div className="container mt-4">
            <div className="alert alert-danger">
              No tienes permisos para acceder a esta sección.
            </div>
          </div>
        </div>
      );
    }

    if (loading || !this.props.customers || !this.props.emailTemplates) {
      return <LoadingProgress />;
    }

    if (error) {
      return (
        <div className="email-templates-view">
          <div className="container mt-4">
            <div className="alert alert-danger">
              <i className="fas fa-exclamation-triangle mr-2"></i>
              {error}
            </div>
            <button
              className="btn btn-primary"
              onClick={() => {
                this.props.fetchCustomersName();
                this.props.fetchEmailTemplates();
              }}
            >
              <i className="fas fa-sync mr-2"></i>
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    const filteredCustomers = this.getFilteredCustomers();

    return (
      <div className="email-templates-view">
        <div className="container-fluid mt-4">
          <div className="email-templates-header">
            <div className="header-content">
              <div className="header-text">
                <h1>
                  <i className="fas fa-envelope mr-3"></i>
                  Plantillas de Email
                </h1>
                <span className="subtitle-inline">
                  Gestiona las plantillas de email personalizadas para cada
                  cliente
                </span>
              </div>
              <div className="search-input-wrapper">
                <i className="fas fa-search search-icon"></i>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Buscar por nombre..."
                  value={this.state.searchTerm}
                  onChange={this.handleSearchChange}
                />
              </div>
            </div>
          </div>


          <div className="templates-container">
            <div className="templates-info">
              {filteredCustomers.filter((c) => c.hasTemplate).length}{" "}
              plantillas creadas de{" "}
              {this.props.customers ? this.props.customers.filter(c => c.Type === 'GENERAL' || c.Type === 'LICENCIA_BASICA' || c.Type === 'LICENCIA_PRO').length : 0} clientes
            </div>

            <div className="templates-grid">
              {filteredCustomers.map((customer) => (
                <Link
                  key={customer._id}
                  to={`/email-template/${customer._id}`}
                  className="template-card"
                >
                  <div className="template-card-header">
                    <div className="customer-avatar">
                      {customer.Fullname
                        ? customer.Fullname.charAt(0).toUpperCase()
                        : "?"}
                    </div>
                    <div className="customer-info">
                      <h3 className="customer-name">{customer.Fullname}</h3>
                      <p className="customer-email">{customer.Email}</p>
                    </div>
                  </div>

                  <div className="template-card-body">
                    <div className="template-status">
                      {customer.hasTemplate ? (
                        <>
                          <i className="fas fa-check-circle text-success mr-2"></i>
                          <span className="status-text">
                            Plantilla configurada
                          </span>
                          <div className="template-dates">
                            <small>
                              Actualizado:{" "}
                              {new Date(
                                customer.template.updatedAt,
                              ).toLocaleDateString("es-ES")}
                            </small>
                          </div>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-exclamation-circle text-warning mr-2"></i>
                          <span className="status-text">Sin plantilla</span>
                          <div className="template-dates">
                            <small>Click para crear una plantilla</small>
                          </div>
                        </>
                      )}
                    </div>
                  </div>

                  <div className="template-card-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span className="btn-edit">
                      {customer.hasTemplate ? (
                        <>
                          <i className="fas fa-edit mr-2"></i>
                          Editar plantilla
                        </>
                      ) : (
                        <>
                          <i className="fas fa-plus mr-2"></i>
                          Crear plantilla
                        </>
                      )}
                    </span>
                    
                    {customer.hasTemplate && (
                      <div className="template-active-toggle" onClick={(e) => this.handleActiveToggle(customer.template, e)}>
                        <div
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            backgroundColor: customer.template.is_active ? '#28a745' : '#dc3545',
                            borderRadius: '25px',
                            padding: '4px',
                            cursor: 'pointer',
                            width: '90px',
                            height: '32px',
                            position: 'relative',
                            transition: 'background-color 0.3s ease',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                          }}
                          title={customer.template.is_active ? 'Click para desactivar' : 'Click para activar'}
                        >
                          <div
                            style={{
                              position: 'absolute',
                              backgroundColor: 'white',
                              borderRadius: '50%',
                              width: '24px',
                              height: '24px',
                              transition: 'transform 0.3s ease',
                              transform: customer.template.is_active ? 'translateX(60px)' : 'translateX(2px)',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                            }}
                          />
                          <span
                            style={{
                              color: 'white',
                              fontSize: '10px',
                              fontWeight: 'bold',
                              position: 'absolute',
                              left: customer.template.is_active ? '8px' : 'auto',
                              right: customer.template.is_active ? 'auto' : '8px',
                              userSelect: 'none',
                            }}
                          >
                            {customer.template.is_active ? 'ACTIVA' : 'INACTIVA'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </Link>
              ))}
            </div>

            {filteredCustomers.length === 0 && (
              <div className="no-results">
                <i className="fas fa-search fa-3x mb-3"></i>
                <h3>No se encontraron clientes</h3>
                <p>Intenta con otro término de búsqueda</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  auth: state.auth,
  role: state.auth.role,
  customers: state.customers.customers,
  customersNeedRefresh: state.customers.customersNeedRefresh,
  emailTemplates: state.emailTemplates.emailTemplates,
  emailTemplatesNeedRefresh: state.emailTemplates.emailTemplatesNeedRefresh,
  loading: state.customers.loading || state.emailTemplates.loading,
  error: state.customers.error || state.emailTemplates.error,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      fetchCustomersName,
      fetchEmailTemplates,
      updateEmailTemplate,
    },
    dispatch,
  );

export default connect(mapStateToProps, mapDispatchToProps)(EmailTemplatesView);
